<!-- components/courses/CoursePreview.vue -->
<template>
  <div class="course-preview">
    <div class="preview-container">
      <div class="preview-header">
        <h2>Course Preview</h2>
        <p>See how your course will look to students</p>
        <div class="preview-actions">
          <button class="btn-secondary" @click="openPreviewInNewTab">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
              />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            Open in New Tab
          </button>
          <button
            class="btn-primary"
            @click="$router.push(`/course/${course._id}`)"
          >
            View Live
          </button>
        </div>
      </div>

      <!-- Mobile Preview -->
      <div class="preview-frame">
        <div class="mobile-mockup">
          <div class="mockup-header">
            <span class="time">9:41</span>
            <div class="status-icons">
              <span>📶</span>
              <span>📶</span>
              <span>🔋</span>
            </div>
          </div>

          <div class="mockup-content">
            <!-- Course Header -->
            <div class="mockup-course-header">
              <h1>{{ course.title || "Untitled Course" }}</h1>
              <p>{{ course.description || "No description yet" }}</p>
              <div class="mockup-meta">
                <span>👨‍🏫 {{ course.creatorName || "Creator" }}</span>
                <span>⭐ {{ course.rating?.toFixed(1) || "New" }}</span>
                <span>📚 {{ course.totalModules || 0 }} modules</span>
              </div>
            </div>

            <!-- Course Stats -->
            <div class="mockup-stats">
              <div class="stat">
                <span class="number">{{ course.totalStudents || 0 }}</span>
                <span class="label">Students</span>
              </div>
              <div class="stat">
                <span class="number">{{ course.totalVideos || 0 }}</span>
                <span class="label">Videos</span>
              </div>
              <div class="stat">
                <span class="number">{{ course.totalDuration || "0h" }}</span>
                <span class="label">Duration</span>
              </div>
            </div>

            <!-- Course Content Preview -->
            <div class="mockup-content-preview">
              <h3>Course Content</h3>
              <div
                v-for="(section, index) in course.sections?.slice(0, 3)"
                :key="section.id"
                class="mockup-section"
              >
                <div class="mockup-section-header">
                  <span class="section-number">{{ index + 1 }}.</span>
                  <span>{{ section.title || "Untitled Section" }}</span>
                  <span class="module-count"
                    >{{ section.modules?.length || 0 }} modules</span
                  >
                </div>
                <div class="mockup-modules">
                  <div
                    v-for="module in section.modules?.slice(0, 2)"
                    :key="module.id"
                    class="mockup-module"
                  >
                    <span class="module-icon">{{
                      module.type === "video" ? "🎬" : "📄"
                    }}</span>
                    <span>{{ module.title || "Untitled Module" }}</span>
                  </div>
                  <div v-if="section.modules?.length > 2" class="mockup-more">
                    +{{ section.modules.length - 2 }} more modules
                  </div>
                </div>
              </div>
              <div
                v-if="course.sections?.length > 3"
                class="mockup-more-sections"
              >
                +{{ course.sections.length - 3 }} more sections
              </div>
            </div>

            <!-- Enroll Button -->
            <div class="mockup-enroll">
              <div class="mockup-price">
                <span v-if="course.price > 0" class="price"
                  >${{ course.price.toFixed(2) }}</span
                >
                <span v-else class="free-badge">Free</span>
              </div>
              <button class="mockup-enroll-btn">Enroll Now</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Preview Details -->
      <div class="preview-details">
        <div class="detail-grid">
          <div class="detail-item">
            <span class="label">Course ID</span>
            <span class="value">{{ course._id || "Not set" }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Status</span>
            <span
              class="value"
              :class="course.isPublished ? 'published' : 'draft'"
            >
              {{ course.isPublished ? "✅ Published" : "📝 Draft" }}
            </span>
          </div>
          <div class="detail-item">
            <span class="label">Category</span>
            <span class="value">{{ course.category || "Not set" }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Difficulty</span>
            <span class="value">{{ course.difficulty || "Not set" }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Created</span>
            <span class="value">{{ formatDate(course.createdAt) }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Last Updated</span>
            <span class="value">{{ formatDate(course.updatedAt) }}</span>
          </div>
        </div>
      </div>

      <!-- Preview Checklist -->
      <div class="preview-checklist">
        <h3>📋 Course Readiness Checklist</h3>
        <div class="checklist-grid">
          <div class="checklist-item" :class="{ complete: course.title }">
            <span class="icon">{{ course.title ? "✅" : "⬜" }}</span>
            <span>Course title set</span>
          </div>
          <div class="checklist-item" :class="{ complete: course.description }">
            <span class="icon">{{ course.description ? "✅" : "⬜" }}</span>
            <span>Course description set</span>
          </div>
          <div class="checklist-item" :class="{ complete: course.thumbnail }">
            <span class="icon">{{ course.thumbnail ? "✅" : "⬜" }}</span>
            <span>Thumbnail uploaded</span>
          </div>
          <div class="checklist-item" :class="{ complete: course.category }">
            <span class="icon">{{ course.category ? "✅" : "⬜" }}</span>
            <span>Category selected</span>
          </div>
          <div
            class="checklist-item"
            :class="{ complete: course.sections?.length > 0 }"
          >
            <span class="icon">{{
              course.sections?.length > 0 ? "✅" : "⬜"
            }}</span>
            <span>At least one section created</span>
          </div>
          <div
            class="checklist-item"
            :class="{ complete: course.totalModules > 0 }"
          >
            <span class="icon">{{
              course.totalModules > 0 ? "✅" : "⬜"
            }}</span>
            <span>At least one module created</span>
          </div>
          <div
            class="checklist-item"
            :class="{
              complete: course.sections?.some((s) =>
                s.modules.some((m) => m.isPublished),
              ),
            }"
          >
            <span class="icon">{{
              course.sections?.some((s) => s.modules.some((m) => m.isPublished))
                ? "✅"
                : "⬜"
            }}</span>
            <span>At least one published module</span>
          </div>
          <div class="checklist-item" :class="{ complete: course.isPublished }">
            <span class="icon">{{ course.isPublished ? "✅" : "⬜" }}</span>
            <span>Course published</span>
          </div>
        </div>
        <div class="checklist-progress">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: readinessPercentage + '%' }"
            ></div>
          </div>
          <span class="progress-text">{{ readinessPercentage }}% Complete</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";

const props = defineProps({
  course: {
    type: Object,
    required: true,
  },
});

const router = useRouter();

const readinessPercentage = computed(() => {
  const checks = [
    !!props.course.title,
    !!props.course.description,
    !!props.course.thumbnail,
    !!props.course.category,
    props.course.sections?.length > 0,
    props.course.totalModules > 0,
    props.course.sections?.some((s) => s.modules.some((m) => m.isPublished)),
    !!props.course.isPublished,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
});
const openPreviewInNewTab = () => {
  // If course is published, go to public course page
  // Otherwise, go to preview page
  const route = props.course.isPublished
    ? router.resolve(`/course/${props.course.id}`)
    : router.resolve(`/course/preview/${props.course.id}`);
  window.open(route.href, "_blank");
};

// Or if you want to preview within the app
const goToCourse = () => {
  // For published courses
  if (props.course.isPublished) {
    router.push(`/course/${props.course.id}`);
  } else {
    // For draft courses, use the preview route
    router.push(`/course/preview/${props.course.id}`);
  }
};

const formatDate = (timestamp) => {
  if (!timestamp) return "Not set";
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;
$primary-blue: #001829;

.course-preview {
  .preview-container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .preview-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;

    h2 {
      margin: 0 0 4px 0;
      color: #1a1a1a;
      font-size: 24px;
    }

    p {
      margin: 0;
      color: #868e96;
    }

    .preview-actions {
      display: flex;
      gap: 8px;

      .btn-secondary {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 16px;
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        font-weight: 600;
        color: #495057;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: #f1f3f5;
        }
      }

      .btn-primary {
        padding: 8px 24px;
        background: $primary-orange;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: darken($primary-orange, 10%);
        }
      }
    }
  }

  .preview-frame {
    background: #f8f9fa;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    border: 1px solid #e9ecef;
    display: flex;
    justify-content: center;

    .mobile-mockup {
      width: 375px;
      background: #fff;
      border-radius: 40px;
      overflow: hidden;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);

      .mockup-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 24px;
        background: #1a1a1a;
        color: #fff;
        font-size: 14px;
        font-weight: 600;

        .status-icons {
          display: flex;
          gap: 4px;
        }
      }

      .mockup-content {
        padding: 16px;
        max-height: 600px;
        overflow-y: auto;

        .mockup-course-header {
          margin-bottom: 16px;

          h1 {
            font-size: 20px;
            font-weight: 700;
            color: #1a1a1a;
            margin: 0 0 8px 0;
          }

          p {
            font-size: 14px;
            color: #495057;
            margin: 0 0 12px 0;
            line-height: 1.5;
          }

          .mockup-meta {
            display: flex;
            gap: 12px;
            font-size: 13px;
            color: #868e96;
          }
        }

        .mockup-stats {
          display: flex;
          justify-content: space-around;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 16px;

          .stat {
            text-align: center;

            .number {
              display: block;
              font-weight: 700;
              color: #1a1a1a;
              font-size: 18px;
            }

            .label {
              font-size: 12px;
              color: #868e96;
            }
          }
        }

        .mockup-content-preview {
          margin-bottom: 16px;

          h3 {
            font-size: 16px;
            color: #1a1a1a;
            margin: 0 0 12px 0;
          }

          .mockup-section {
            margin-bottom: 8px;

            .mockup-section-header {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 6px 12px;
              background: #f8f9fa;
              border-radius: 6px;
              font-size: 14px;
              font-weight: 500;
              color: #1a1a1a;

              .section-number {
                color: $primary-orange;
                font-weight: 700;
              }

              .module-count {
                font-size: 12px;
                color: #868e96;
                font-weight: 400;
                margin-left: auto;
              }
            }

            .mockup-modules {
              padding: 4px 0 4px 28px;

              .mockup-module {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 8px;
                font-size: 13px;
                color: #495057;

                .module-icon {
                  font-size: 14px;
                }
              }

              .mockup-more {
                padding: 4px 8px;
                font-size: 12px;
                color: #868e96;
                font-style: italic;
              }
            }
          }

          .mockup-more-sections {
            padding: 4px 12px;
            font-size: 13px;
            color: #868e96;
            font-style: italic;
          }
        }

        .mockup-enroll {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px;
          background: $primary-blue;
          border-radius: 8px;

          .mockup-price {
            .price {
              font-size: 24px;
              font-weight: 700;
              color: #fff;
            }

            .free-badge {
              font-size: 20px;
              font-weight: 700;
              color: #28a745;
            }
          }

          .mockup-enroll-btn {
            padding: 8px 24px;
            background: $primary-orange;
            color: #fff;
            border: none;
            border-radius: 8px;
            font-weight: 600;
            cursor: default;
          }
        }
      }
    }
  }

  .preview-details {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    border: 1px solid #e9ecef;

    .detail-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 16px;

      .detail-item {
        .label {
          display: block;
          font-size: 12px;
          color: #868e96;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .value {
          font-size: 14px;
          font-weight: 600;
          color: #1a1a1a;

          &.published {
            color: #28a745;
          }

          &.draft {
            color: #ffc107;
          }
        }
      }
    }
  }

  .preview-checklist {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 24px;
    border: 1px solid #e9ecef;

    h3 {
      margin: 0 0 16px 0;
      color: #1a1a1a;
      font-size: 18px;
    }

    .checklist-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 8px;
      margin-bottom: 16px;

      .checklist-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        background: #fff;
        border-radius: 6px;
        font-size: 14px;
        color: #868e96;
        transition: all 0.2s ease;

        &.complete {
          color: #28a745;
          background: rgba(40, 167, 69, 0.05);
        }

        .icon {
          font-size: 16px;
        }
      }
    }

    .checklist-progress {
      .progress-bar {
        width: 100%;
        height: 8px;
        background: #e9ecef;
        border-radius: 4px;
        overflow: hidden;

        .progress-fill {
          height: 100%;
          background: $primary-orange;
          border-radius: 4px;
          transition: width 0.3s ease;
        }
      }

      .progress-text {
        display: block;
        text-align: center;
        font-size: 14px;
        font-weight: 600;
        color: #1a1a1a;
        margin-top: 8px;
      }
    }
  }
}
</style>
